'use client';

import { fetchpatientParamsDictionary } from '@/layers/presentation/services/patients';
import logger from '@/layers/support/monitoring/logger';
import { UserType } from '@/types/modelEnums';
import {
  PatientParam,
  PatientParamValues,
  PatientProfile,
  ProviderProfile,
  SponsorProfile,
  User,
  ValidProfiles,
} from '@/types/models';
import { usePathname } from 'next/navigation';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const LOG_MODULE = 'FE [Presentation / Context / GlobalStateContext]';

interface GlobalStateContextType {
  patientParamsDictionary: PatientParam[];
  isLoadingParams: boolean;
  error: string | null;
  user: User | null;
  profile: ValidProfiles | null;
  selectedOptions: PatientParamValues;
  query: string | undefined;
  setUser: (user: User | null) => void;
  setProfile: (profile: ValidProfiles | null) => void;
  getProviderProfile: () => ProviderProfile | null;
  getPatientProfile: () => PatientProfile | null;
  getSponsorProfile: () => SponsorProfile | null;
  setSelectedOptions: React.Dispatch<React.SetStateAction<PatientParamValues>>;
  setQuery: (query: string | undefined) => void;
  isProfileLoaded: boolean;
  setIsProfileLoaded: (isProfileLoaded: boolean) => void;
  loadPatientParamsDictionary: () => Promise<void>;
}

const GlobalStateContext = createContext<GlobalStateContextType | undefined>(undefined);

export const GlobalStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const pathname = usePathname();
  const isAppRoute = useMemo(() => {
    return (
      pathname.startsWith('/patient') ||
      pathname.startsWith('/provider') ||
      pathname.startsWith('/sponsor') ||
      pathname.startsWith('/users') ||
      pathname.startsWith('/admin')
    );
  }, [pathname]);

  const [patientParamsDictionary, setPatientParamsDictionary] = useState<PatientParam[]>([]);
  const [isLoadingParams, setIsLoadingParams] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ValidProfiles | null>(null);
  const [isProfileLoaded, setIsProfileLoaded] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<PatientParamValues>({});

  // Never fetch programs with undefined query
  const [query, setQuery] = useState<string | undefined>(undefined);

  const getProviderProfile = (): ProviderProfile | null => {
    return user?.type === UserType.PROVIDER ? (profile as ProviderProfile) : null;
  };

  const getPatientProfile = (): PatientProfile | null => {
    return user?.type === UserType.PATIENT ? (profile as PatientProfile) : null;
  };

  const getSponsorProfile = (): SponsorProfile | null => {
    return user?.type === UserType.SPONSOR ? (profile as SponsorProfile) : null;
  };

  useEffect(() => {
    logger.debug('Global state updated', {
      module: LOG_MODULE,
      data: {
        patientParamsDictionaryCount: patientParamsDictionary.length,
        isLoadingParams,
        error,
        user,
        profile,
      },
    });
  }, [patientParamsDictionary, isLoadingParams, error, user, profile]);

  const isFetchingParamsRef = useRef(false);
  const loadPatientParamsDictionary = useCallback(async () => {
    if (isFetchingParamsRef.current) return;
    if (patientParamsDictionary.length > 0) return;

    isFetchingParamsRef.current = true;
    setIsLoadingParams(true);

    try {
      const { patientParamsDictionary } = await fetchpatientParamsDictionary();
      setPatientParamsDictionary(patientParamsDictionary);
      setError(null);
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setIsLoadingParams(false);
      isFetchingParamsRef.current = false;
    }
  }, [patientParamsDictionary.length]);

  useEffect(() => {
    if (!isAppRoute) return;
    loadPatientParamsDictionary().catch(() => undefined);
  }, [isAppRoute, loadPatientParamsDictionary]);

  return (
    <GlobalStateContext.Provider
      value={{
        patientParamsDictionary,
        isLoadingParams,
        error,
        user,
        profile,
        setUser,
        setProfile,
        getProviderProfile,
        getPatientProfile,
        getSponsorProfile,
        selectedOptions,
        query,
        setSelectedOptions,
        setQuery,
        isProfileLoaded,
        setIsProfileLoaded,
        loadPatientParamsDictionary,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};

export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider');
  }
  return context;
};
